import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/types/supabase";
import { throwEdgeFnError } from "@/lib/edgeFnError";

type Client = SupabaseClient<Database>;

// The document hash is retained server-side as audit evidence and is never
// delivered to browser clients, including the admin list.
const BETA_SIGNUP_ADMIN_SELECT =
  "id, full_name, email, committed, privacy_notice_version, privacy_notice_acknowledged_at, status, created_at, approved_by, approved_at";

export interface BetaSignupRow {
  id: string;
  full_name: string;
  email: string;
  committed: boolean;
  /** Evidence that the beta-specific privacy notice was shown; not consent. */
  privacy_notice_version: string | null;
  privacy_notice_acknowledged_at: string | null;
  status: "pending" | "approved" | "rejected";
  created_at: string;
  approved_by: string | null;
  approved_at: string | null;
}

export interface SubmitBetaSignupResult {
  ok: true;
}

export interface SubmitBetaSignupPayload {
  full_name: string;
  email: string;
  committed: boolean;
  /** Explicit notice acknowledgement. This is intentionally not broad consent. */
  privacy_notice_acknowledged: boolean;
  privacy_notice_hash: string;
}

// ─── Public (anon) ─────────────────────────────────────────────────────────

export async function submitBetaSignup(
  client: Client,
  payload: SubmitBetaSignupPayload,
): Promise<SubmitBetaSignupResult> {
  const { data, error } = await client.functions.invoke<
    SubmitBetaSignupResult | { error: string }
  >("submit-beta-signup", {
    body: payload,
  });
  if (error) {
    const context = (error as { context?: unknown }).context;
    if (context instanceof Response) {
      await throwEdgeFnError(context, error.message);
    }
    throw error;
  }
  if (data && "error" in data) throw new Error(data.error);
  return data as SubmitBetaSignupResult;
}

export async function isBetaFormEnabled(client: Client): Promise<boolean> {
  const { data, error } = await client.rpc("is_beta_form_enabled");
  if (error) throw error;
  return Boolean(data);
}

export async function isBetaAutoApproveEnabled(client: Client): Promise<boolean> {
  const { data, error } = await client.rpc("is_beta_auto_approve");
  if (error) throw error;
  return Boolean(data);
}

export async function setBetaAutoApprove(
  client: Client,
  enabled: boolean,
): Promise<void> {
  const { error } = await client.rpc("set_beta_auto_approve", { p_enabled: enabled });
  if (error) throw error;
}

// ─── Admin ─────────────────────────────────────────────────────────────────

export async function fetchBetaSignups(client: Client): Promise<BetaSignupRow[]> {
  const { data, error } = await client
    .from("beta_signups")
    .select(BETA_SIGNUP_ADMIN_SELECT)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as BetaSignupRow[];
}

export interface ApproveResult {
  emailSent: boolean;
  alreadyUser: boolean;
  error?: string;
}

export async function approveBetaSignup(
  client: Client,
  id: string,
): Promise<ApproveResult> {
  const { error } = await client.rpc("approve_beta_signup", { p_id: id });
  if (error) throw error;

  // Fire Clerk invitation email. Best-effort: approval already mutated DB.
  // If the invite fails (rate limit, secret missing) we surface a warning but
  // do not roll back the approval — admin can resend manually later.
  const { data, error: inviteErr } = await client.functions.invoke<{
    ok?: boolean;
    already_user?: boolean;
    error?: string;
  }>("notify-beta-approval", {
    body: { signup_id: id },
  });

  if (inviteErr) {
    return { emailSent: false, alreadyUser: false, error: inviteErr.message };
  }
  if (data?.error) {
    return { emailSent: false, alreadyUser: false, error: data.error };
  }
  return {
    emailSent: Boolean(data?.ok && !data.already_user),
    alreadyUser: Boolean(data?.already_user),
  };
}

export async function rejectBetaSignup(client: Client, id: string): Promise<void> {
  const { error } = await client.rpc("reject_beta_signup", { p_id: id });
  if (error) throw error;
}

export interface BulkApproveResult {
  approved: number;
  emailsSent: number;
  alreadyUsers: number;
  failed: number;
}

/**
 * Bulk-approve all pending signups. Iterates client-side instead of using the
 * SQL loop RPC so each row also triggers a Clerk invitation email.
 */
export async function approveAllPending(client: Client): Promise<BulkApproveResult> {
  const { data: pending, error } = await client
    .from("beta_signups")
    .select("id")
    .eq("status", "pending");
  if (error) throw error;

  const out: BulkApproveResult = { approved: 0, emailsSent: 0, alreadyUsers: 0, failed: 0 };
  for (const row of pending ?? []) {
    try {
      const res = await approveBetaSignup(client, row.id as string);
      out.approved += 1;
      if (res.emailSent) out.emailsSent += 1;
      if (res.alreadyUser) out.alreadyUsers += 1;
      if (res.error) out.failed += 1;
    } catch {
      out.failed += 1;
    }
  }
  return out;
}

export async function setBetaFormEnabled(
  client: Client,
  enabled: boolean,
): Promise<void> {
  const { error } = await client.rpc("set_beta_form_enabled", {
    p_enabled: enabled,
  });
  if (error) throw error;
}
